import Swiper from 'swiper';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';

class ImageHeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    this.initSlider();
  }

  /**
   * Updates focusability of slide content so only the active slide's
   * interactive elements are tabbable. Inactive slides are marked
   * aria-hidden and their focusable descendants get tabindex="-1".
   */
  updateSlideFocusability() {
    if (!this.swiper) return;

    // Use Swiper's slides array which handles loop mode correctly
    const slides = this.swiper.slides;
    if (!slides || !slides.length) return;

    const focusableSelectors =
      'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])';

    slides.forEach((slide, index) => {
      // In loop mode, use realIndex; Swiper tracks the actual active slide
      const isActive = index === this.swiper.activeIndex;
      const focusableEls = slide.querySelectorAll(focusableSelectors);

      if (isActive) {
        // Active slide: remove aria-hidden, restore tabindex
        slide.removeAttribute('aria-hidden');
        focusableEls.forEach((el) => {
          const prevTabindex = el.getAttribute('data-prev-tabindex');
          if (prevTabindex !== null) {
            if (prevTabindex === '') {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', prevTabindex);
            }
            el.removeAttribute('data-prev-tabindex');
          } else if (el.getAttribute('tabindex') === '-1') {
            // If we set it to -1 but there's no data-prev-tabindex, remove it
            el.removeAttribute('tabindex');
          }
        });
      } else {
        // Inactive slide: hide from assistive tech, make descendants unfocusable
        slide.setAttribute('aria-hidden', 'true');
        focusableEls.forEach((el) => {
          const currentTabindex = el.getAttribute('tabindex');
          if (currentTabindex !== '-1') {
            // Store the previous tabindex so we can restore it
            el.setAttribute(
              'data-prev-tabindex',
              currentTabindex !== null ? currentTabindex : ''
            );
            el.setAttribute('tabindex', '-1');
          }
        });
      }
    });
  }

  initSlider() {
    const sliderContainer = this.querySelector('.swiper');
    if (!this.swiper && sliderContainer) {
      const context = this;
      const autoplayEnabled = this.getAttribute('data-autoplay') !== 'false';

      this.swiper = new Swiper(sliderContainer, {
        slidesPerView: 1,
        modules: [Pagination, Navigation, Autoplay],
        loop: true,
        autoHeight: true,
        autoplay: autoplayEnabled
          ? {
              delay: 3850,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          clickable: true,
          // Render pagination bullets as actual <button> elements for keyboard accessibility
          renderBullet: function (index, className) {
            return `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"></button>`;
          },
        },
        navigation: {
          nextEl: '.swiper-video-next',
          prevEl: '.swiper-video-prev',
        },
        on: {
          init: function () {
            // Set initial slide focusability after Swiper initializes
            // Use requestAnimationFrame to ensure DOM classes are applied
            requestAnimationFrame(() => {
              context.updateSlideFocusability();
            });
            // Setup mobile video interaction handlers
            context.setupMobileVideoHandlers();
          },
          slideChangeTransitionEnd: function () {
            // Update focusability after slide transition completes
            // This ensures the active class is fully applied
            context.updateSlideFocusability();
            // Re-setup video handlers for the new active slide
            context.setupMobileVideoHandlers();
          },
        },
      });

      if (autoplayEnabled) {
        // Stop autoplay when keyboard focus enters the carousel (WCAG 2.2.2)
        // This ensures the slide stays visible while the user is interacting with it
        this.addEventListener('focusin', () => {
          if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) {
            this.swiper.autoplay.stop();
          }
        });

        // Restart autoplay when focus leaves the carousel entirely
        this.addEventListener('focusout', (e) => {
          // Check if the new focus target is still within this carousel
          // Use setTimeout to allow the new activeElement to be set
          setTimeout(() => {
            if (!this.contains(document.activeElement)) {
              if (this.swiper && this.swiper.autoplay) {
                this.swiper.autoplay.start();
              }
            }
          }, 0);
        });
      }
    }
  }

  /**
   * Sets up event handlers for mobile videos to pause autoplay when user interacts.
   * This prevents the carousel from auto-scrolling while a user is watching a video.
   */
  setupMobileVideoHandlers() {
    const mobileVideos = this.querySelectorAll('[data-video-mobile-index]');
    const context = this;

    mobileVideos.forEach((video) => {
      // Skip if already set up
      if (video.dataset.mobileHandlerAttached) return;
      video.dataset.mobileHandlerAttached = 'true';

      // Handle user interaction with mobile video (touch/click)
      const handleVideoInteraction = () => {
        // Only apply on mobile viewport
        if (window.innerWidth < 1110) {
          if (context.swiper && context.swiper.autoplay) {
            context.swiper.autoplay.stop();
          }
          // Set video to loop so user can keep watching
          video.setAttribute('loop', 'true');
        }
      };

      // Listen for user interactions on the video
      video.addEventListener('click', handleVideoInteraction);
      video.addEventListener('touchstart', handleVideoInteraction, { passive: true });
    });
  }
}

customElements.define('image-hero-carousel', ImageHeroCarousel);
